/**
 * Blood Test panel carousel — biomarker modal.
 *
 * Built on the native <dialog> element, so focus trapping, Esc-to-close, background
 * inerting and focus restoration come from the platform rather than hand-rolled JS.
 * This module only wires the triggers, the backdrop click, and the background scroll
 * lock (which showModal() does not do on its own).
 *
 * Contract:
 *   [data-bt-modal-open="<dialog id>"]   trigger (a button stretched over the card)
 *   <dialog data-bt-modal>               the modal
 *     [data-bt-modal-close]              close button
 *     [data-bt-modal-panel]              inner panel — clicks outside it close
 */

const SCROLL_LOCK_CLASS = 'bt-modal-open';

const lock = () => document.documentElement.classList.add(SCROLL_LOCK_CLASS);
const unlock = () => document.documentElement.classList.remove(SCROLL_LOCK_CLASS);

function open(dialog) {
  // Cancel any in-flight close: without this a reopen inherits the leftover exit
  // animation / a pending animationend finisher and closes itself a moment later.
  dialog.classList.remove(CLOSING_CLASS);
  if (typeof dialog.showModal === 'function') {
    if (!dialog.open) dialog.showModal();
  } else {
    // No <dialog> support: show it inline rather than trapping the user in a modal
    // they have no way to dismiss.
    dialog.setAttribute('open', '');
  }
  lock();
}

const CLOSING_CLASS = 'blood-test-modal--closing';

/**
 * close() yanks the dialog out of the top layer immediately, so an exit animation on
 * [open] would never render. Flag it, let the CSS animation run, then really close.
 */
function requestClose(dialog) {
  if (!dialog.open || dialog.classList.contains(CLOSING_CLASS)) return;

  const finish = () => {
    // If the modal was reopened in the meantime, open() cleared the class — abort so a
    // stale animationend/timeout from this close doesn't shut the fresh modal.
    if (!dialog.classList.contains(CLOSING_CLASS)) return;
    dialog.classList.remove(CLOSING_CLASS);
    dialog.close();
  };

  dialog.classList.add(CLOSING_CLASS);

  const styles = window.getComputedStyle(dialog);
  const hasAnimation = styles.animationName && styles.animationName !== 'none';

  // No animation (reduced motion, unsupported, or the class did not take): close now
  // rather than leaving the user stuck behind an open modal.
  if (!hasAnimation) {
    finish();
    return;
  }

  dialog.addEventListener('animationend', finish, { once: true });
  // Belt and braces: if animationend never fires the modal must still close.
  window.setTimeout(() => {
    if (dialog.classList.contains(CLOSING_CLASS)) finish();
  }, 400);
}

function bind(dialog) {
  // Listeners are attached once per element. Re-running init must stay a no-op here
  // but must NOT stop the dialog being resolvable by id below.
  if (dialog.dataset.btModalReady === 'true') return;
  dialog.dataset.btModalReady = 'true';

  const panel = dialog.querySelector('[data-bt-modal-panel]');

  // A click landing on the <dialog> itself is a click on the backdrop: the panel
  // covers the whole dialog box, so anything else would have hit the panel.
  dialog.addEventListener('click', (e) => {
    if (panel && e.target === dialog) requestClose(dialog);
  });

  // Esc dismisses via `cancel`; intercept so it animates out like every other route.
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    requestClose(dialog);
  });

  dialog.addEventListener('close', unlock);

  dialog.querySelectorAll('[data-bt-modal-close]').forEach((btn) => {
    btn.addEventListener('click', () => requestClose(dialog));
  });
}

/** Open a [data-bt-modal] dialog from other modules (e.g. meeting-form-links.js). */
export function openModal(dialog) {
  bind(dialog);
  open(dialog);
}

export default function initBloodTestPanelModal(scope) {
  const root = scope && scope.querySelectorAll ? scope : document;

  const dialogs = [...root.querySelectorAll('[data-bt-modal]')];
  dialogs.forEach(bind);

  const onTrigger = (e) => {
    const el = e.currentTarget;
    // Two trigger shapes: the section-rendered buttons carry data-bt-modal-open, while
    // links typed into a richtext setting can only carry an href — Shopify rejects data
    // attributes in richtext, so `<a href="#bt-marker-modal">` is the merchant-facing form.
    const id = el.dataset.btModalOpen || (el.getAttribute('href') || '').replace(/^#/, '');
    // Resolve against the document, not a cached map: the dialog is a sibling of the
    // trigger and both may have been re-rendered by the theme editor.
    const dialog = id && document.getElementById(id);
    if (!dialog || !dialog.matches('[data-bt-modal]')) return;
    e.preventDefault();
    bind(dialog);
    open(dialog);
  };

  const hashLinks = [...root.querySelectorAll('a[href^="#"]')].filter((a) => {
    const target = document.getElementById(a.getAttribute('href').slice(1));
    return target && target.matches('[data-bt-modal]');
  });

  const triggers = [...root.querySelectorAll('[data-bt-modal-open]'), ...hashLinks];
  triggers.forEach((t) => {
    if (t.dataset.btTriggerReady === 'true') return;
    t.dataset.btTriggerReady = 'true';
    t.addEventListener('click', onTrigger);
  });

  return {
    destroy() {
      triggers.forEach((t) => {
        t.removeEventListener('click', onTrigger);
        delete t.dataset.btTriggerReady;
      });
      dialogs.forEach((d) => {
        if (d.open) {
          d.classList.remove(CLOSING_CLASS);
          d.close();
        }
      });
      unlock();
    },
  };
}
